/**
 * Focus helpers shared by the modal surfaces.
 *
 * Visibility is decided from attributes only, never from layout. `offsetParent`
 * and `getClientRects()` both report "invisible" under jsdom, which would make
 * the focus trap silently do nothing in tests while working in the browser.
 * Everything we render hides content by unmounting it, so attributes are enough.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  'iframe',
  'object',
  'embed',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');

/** True when `element` can hold DOM focus. `aria-disabled` stays focusable by design. */
export function isFocusable(element: HTMLElement): boolean {
  if (element.hasAttribute('disabled')) return false;
  if (element.hidden) return false;
  if (element.getAttribute('aria-hidden') === 'true') return false;
  if (element.closest('[inert]') !== null) return false;
  if (element.closest('[hidden]') !== null) return false;

  const tabIndex = element.getAttribute('tabindex');
  if (tabIndex !== null && Number.parseInt(tabIndex, 10) < 0) return false;

  return true;
}

/** Focusable descendants of `root`, in document order. */
export function getFocusableElements(root: HTMLElement): HTMLElement[] {
  const candidates = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return candidates.filter(isFocusable);
}
